<template>
  <div ref="container" class="relative">
    <div
      v-for="point in positionedPoints"
      :key="point.items.map((item) => item.id).join('-')"
      class="ease-fast-spatial absolute transition-all duration-500"
      :class="[!animate && 'invisible']"
      :style="positionStyle(point)"
    >
      <slot :item="point.item" :items="point.items" :count="point.items.length" />
    </div>
  </div>
</template>
<script setup lang="ts" generic="T extends { id: string }">
  import { computed, onMounted, ref, useTemplateRef } from 'vue'
  import { useElementBounding } from '@vueuse/core'
  import { positionPoints, type PositionedPoint } from './distribute-items'

  interface Props {
    size?: number
    padding?: number
    direction?: 'horizontal' | 'vertical'
    points: {
      item: T
      value: number
      groupKey?: string
    }[]
    marginPercentage?: number
    collapseOverflow?: boolean
  }

  const {
    size = 40,
    padding = 12,
    direction = 'vertical',
    points,
    marginPercentage = 0,
    collapseOverflow = false,
  } = defineProps<Props>()

  const animate = ref(false)

  onMounted(() => {
    setTimeout(() => {
      animate.value = true
    }, 400)
  })

  const container = useTemplateRef('container')
  const { width, height } = useElementBounding(container)

  const startPercent = computed(() => Math.min(50, Math.max(0, marginPercentage)))
  const rangePercent = computed(() => Math.max(0, 100 - 2 * startPercent.value))

  const mapValuePercent = (value: number) => startPercent.value + value * rangePercent.value

  const positionStyle = (point: PositionedPoint<T>) => {
    const box = {
      width: size + 'px',
      height: size + 'px',
    }
    const centerPercent = animate.value ? mapValuePercent(point.value) : startPercent.value
    const alongValue = `calc(${centerPercent}% - ${size / 2}px)`

    if (direction === 'horizontal') {
      return {
        ...box,
        left: alongValue,
        top: point.y + 'px',
      }
    }

    return {
      ...box,
      left: point.x + 'px',
      bottom: alongValue,
    }
  }

  const positionedPoints = computed(() =>
    positionPoints({
      points,
      size,
      padding,
      direction,
      width: width.value,
      height: height.value,
      startPercent: startPercent.value,
      rangePercent: rangePercent.value,
      collapseOverflow,
    }),
  )
</script>
